import { Collapsible } from 'src/components/ui/collapsible';
import { Controller, useFieldArray } from 'react-hook-form';
import { Switch } from 'src/components/ui/switch';
import React, { useEffect } from 'react';
import Input from 'src/components/Input/Input';
import { clsx } from 'clsx';
import { uuid } from 'src/utils/uuid';

const INITIAL_STATE = {
  key: 'initial',
  id: '',
  name: '',
};

export const IncludeTicketFields = ({
  name = 'include_custom_fields',
  control,
  watch,
  clearErrors,
  inputFieldType = 'number' as 'number' | 'text',
  disabled = false,
}) => {
  const includeTicketSwitch = watch(name);
  const { fields, append, remove } = useFieldArray({
    rules: { required: true },
    control,
    name: `custom_fields`,
  });

  useEffect(() => {
    if (!fields.length) {
      append({ ...INITIAL_STATE });
    }
  }, []);

  const addField = (e) => {
    e.preventDefault();
    append({ ...INITIAL_STATE, key: uuid(), id: '' });
  };

  const removeField = (e, index) => {
    e.preventDefault();
    remove(index);
  };

  return (
    <div>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <span className="flex flex-row gap-4">
            <span className="flex items-center gap-4">
              <Switch id={name} checked={value} onCheckedChange={onChange} disabled={disabled} />
            </span>
            <label
              htmlFor={name}
              className={'cursor-pointer font-semibold text-sm text-ebony_opacity_80'}
            >
              Include ticket fields in the context
            </label>
          </span>
        )}
      />
      <div className={'mt-2.5'}>
        <Collapsible open={includeTicketSwitch} contentClassName={'pl-[60px]'}>
          <div className={'flex flex-col gap-1.5'}>
            {fields.map((field, index, array) => {
              return (
                <div key={(field as any).key || field.id} className={'flex flex-col gap-1.5'}>
                  <div className={'flex flex-row gap-2.5'}>
                    <div className={'flex flex-col flex-1 gap-1.5'}>
                      <Controller
                        control={control}
                        name={`custom_fields.${index}.name`}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <div
                            className={clsx(
                              'flex flex-col gap-1',
                              array.length === 1 ? 'pr-4' : '',
                            )}
                          >
                            <Input
                              readonly={disabled}
                              label={`${index + 1}. Field`}
                              type={'text'}
                              placeholder={'Name'}
                              invalidMessage={error?.message}
                              value={value}
                              isValid={!error?.message}
                              onChange={onChange}
                              inputBlockClassName={'w-full'}
                              className={'!py-3'}
                              onFocus={() => {
                                clearErrors(`custom_fields.${index}`);
                              }}
                            />
                          </div>
                        )}
                      />
                      <Controller
                        control={control}
                        name={`custom_fields.${index}.id`}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <div
                            className={clsx(
                              'flex flex-row gap-2.5',
                              array.length === 1 ? 'pr-4' : '',
                            )}
                          >
                            <Input
                              readonly={disabled}
                              type={inputFieldType}
                              placeholder={'ID'}
                              invalidMessage={error?.message}
                              value={value}
                              isValid={!error?.message}
                              onChange={onChange}
                              inputBlockClassName={'w-full'}
                              className={'!py-3'}
                              onFocus={() => {
                                clearErrors(`custom_fields.${index}`);
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                    <div className={'flex items-center justify-center'}>
                      {!disabled && array.length !== 1 && (
                        <div
                          className={
                            'flex items-center justify-center opacity-30 hover:opacity-100 hover:cursor-pointer pt-6'
                          }
                          onClick={(e) => {
                            removeField(e, index);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="18"
                            viewBox="0 0 16 18"
                            fill="none"
                          >
                            <path
                              d="M1.04405 17.0996V2.93294H0.0996094V1.04405H4.82183V0.0996094H10.4885V1.04405H15.2107V2.93294H14.2663V17.0996H1.04405ZM2.93294 15.2107H12.3774V2.93294H2.93294V15.2107ZM4.82183 13.3218H6.71072V4.82183H4.82183V13.3218ZM8.59961 13.3218H10.4885V4.82183H8.59961V13.3218Z"
                              fill="#05050D"
                              stroke="white"
                              strokeWidth="0.2"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!disabled && (
            <div className={'pr-[26px] mt-1.5'}>
              <button
                disabled={disabled}
                onClick={(e) => {
                  addField(e);
                }}
                className={
                  'py-3.5 px-2.5 text-violet text-start text-xs font-medium bg-light w-full rounded-sm-5 hover:bg-[#ebebeb] cursor-pointer'
                }
              >
                Add Field ID
              </button>
            </div>
          )}
        </Collapsible>
      </div>
    </div>
  );
};
